import { FC, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { UpgradePlanModal } from 'src/components/UpgradePlanModal'
import { SearchInput } from 'src/components/SearchInput'
import { queryClient } from 'src/reactQuery'
import { QueryKey } from 'src/reactQuery/enums/queryKey.enum'
import { useDisconnectPageMutation } from 'src/reactQuery/useDisconnectPageMutation'
import {
  InboxAnalysisPage,
  useGetInboxAnalysisPagesQuery,
} from 'src/reactQuery/useGetInboxAnalysisPagesQuery'
import { useGetSelfQuery } from 'src/reactQuery/useGetSelfQuery'
import { useGetUserFeaturesQuery } from 'src/reactQuery/useGetUserFeaturesQuery'
import { INBOX_BACKEND_URL } from 'src/services/constants/urls'
import { useQueryParams } from 'src/hooks/useQueryParams'
import { createLink } from 'src/utils/createLink'
import { getDateRangeByQueryParams } from 'src/utils/getDateRangeByQueryParams'
import { GlobalQueryParams } from 'src/interfaces/globalQueryParams.interface'
import { InboxAnalysisEmptyState } from './InboxAnalysisEmptyState'
import { InboxOverview } from './InboxOverview'
import { DisconnectPageModal } from './DisconnectPageModal'
import { ConnectAccountModal } from './ConnectAccountModal'
import { PageCard, PageCardSkeleton } from './PageCard'
import classes from './inboxAnalysis.module.scss'
import { SupportContactModal } from '../AIAssistant/SupportContactModal'

// Two cards fill one .cardRow (each is flex 1 1 calc(50% - 12px)).
const PAGE_CARD_SKELETON_COUNT = 2

// The browser's zone, so the cards' Key Insights "Peak Day" is the user's day.
const BROWSER_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone

export const InboxAnalysis: FC = () => {
  const navigate = useNavigate()
  const [searchInput, setSearchInput] = useState('')
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false)
  const [isConnectModalOpen, setIsConnectModalOpen] = useState(false)
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false)
  const [pageToDisconnect, setPageToDisconnect] =
    useState<InboxAnalysisPage | null>(null)

  const { queryParams } = useQueryParams<GlobalQueryParams>()
  const dates = getDateRangeByQueryParams(queryParams)

  const { data: userData } = useGetSelfQuery()
  const { data: userFeatures, isLoading: featuresLoading } =
    useGetUserFeaturesQuery()

  const dateRange = useMemo(() => {
    return {
      fromDate: dates?.start,
      toDate: dates?.end,
    }
  }, [dates?.start, dates?.end])

  const hasAccess = userFeatures?.data?.inboxAnalysisEnabled ?? false

  // Only fetch once features are loaded and the user actually has access —
  // otherwise the backend answers 403 for every visitor without the feature.
  const { data, isLoading, error } = useGetInboxAnalysisPagesQuery(
    { ...dateRange, tz: BROWSER_TIMEZONE },
    { enabled: hasAccess }
  )

  const disconnectMutation = useDisconnectPageMutation({
    onError: () => {
      setPageToDisconnect(null)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.GET_INBOX_ANALYSIS_PAGES],
      })
      setPageToDisconnect(null)
    },
  })

  const handleSearchChange = (val: string) => {
    setSearchInput(val.toLowerCase())
  }

  const handlePageClick = (pageId: string) => {
    const link = createLink(`/inbox-analysis/${pageId}`, {
      queryParams: {
        from: queryParams.from,
        period: queryParams.period,
        to: queryParams.to,
      },
    })
    navigate(link)
  }

  const handleSelectPlatform = () => {
    const userId = userData?.user?._id
    if (!userId) {
      return
    }
    const redirectUrl = `${window.location.origin}/inbox-analysis`
    window.location.href = `${INBOX_BACKEND_URL}/login?hapttic_user_id=${userId}&redirect_url=${encodeURIComponent(redirectUrl)}`
  }

  const filteredPages = useMemo(() => {
    if (!data?.pages) return []
    return data.pages.filter((page) =>
      page.name.toLowerCase().includes(searchInput)
    )
  }, [data?.pages, searchInput])

  if (featuresLoading) {
    return (
      <div className={classes.inboxAnalysisContainer}>
        <div className={classes.content}>
          <div className={classes.emptyState}>
            <p>Checking access permissions...</p>
          </div>
        </div>
      </div>
    )
  }

  if (!hasAccess) {
    return (
      <>
        <div className={classes.inboxAnalysisContainer}>
          <InboxAnalysisEmptyState
            onEnableClick={() => setIsUpgradeModalOpen(true)}
          />
        </div>
        <UpgradePlanModal
          isOpen={isUpgradeModalOpen}
          setIsOpen={setIsUpgradeModalOpen}
        />
      </>
    )
  }

  return (
    <div className={classes.inboxAnalysisContainer}>
      {/* The connected-page cards previously collapsed to a single line of
          text while loading, then the whole section jumped open when the
          pages landed. Render the real card shells instead — two per row,
          matching .cardRow's 50% split. */}
      {/* Rendered during loading too. InboxOverview runs its own summary query
          and carries its own skeletons, but the page used to gate it behind
          !isLoading — so the tall widget was absent while only the small page
          cards below showed, and everything shoved down when it mounted. */}
      {!error && (
        <InboxOverview
          arePagesLoading={isLoading}
          fromDate={dateRange.fromDate}
          pages={data?.pages ?? []}
          toDate={dateRange.toDate}
        />
      )}

      {isLoading && (
        <div className={classes.cards}>
          <div className={classes.cardRow}>
            {Array.from({ length: PAGE_CARD_SKELETON_COUNT }).map(
              (_, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <PageCardSkeleton key={index} />
              )
            )}
          </div>
        </div>
      )}

      {error && (
        <div className={classes.content}>
          <div className={classes.emptyState}>
            <p>Failed to load inbox analysis data</p>
          </div>
        </div>
      )}

      {!isLoading && !error && (
        <>
          <div className={classes.sectionHeader}>
            <h2 className={classes.sectionTitle}>Connected Pages</h2>
            <div className={classes.searchWrapper}>
              <SearchInput debouncedChange={handleSearchChange} />
            </div>
          </div>

          <div className={classes.cards}>
            <div className={classes.cardRow}>
              {filteredPages.map((page) => (
                <PageCard
                  key={page.id}
                  onDisconnect={() => setPageToDisconnect(page)}
                  onOpen={() => handlePageClick(page.id)}
                  page={page}
                />
              ))}

              {/* Connect Account Card */}
              <div className={classes.connectFacebookCard}>
                <button
                  className={classes.connectFacebookButton}
                  onClick={() => setIsConnectModalOpen(true)}
                  type="button"
                >
                  <span className={classes.connectPlusIcon}>+</span>
                  <span className={classes.connectFacebookText}>
                    Connect Account
                  </span>
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      <ConnectAccountModal
        isOpen={isConnectModalOpen}
        onClose={() => setIsConnectModalOpen(false)}
        onContactUs={() => {
          setIsConnectModalOpen(false)
          setIsSupportModalOpen(true)
        }}
        onSelectPlatform={handleSelectPlatform}
      />

      <SupportContactModal
        description={
          'To add a custom platform to your Inbox Analysis, ' +
          'please contact the Hapttic support team.'
        }
        isOpen={isSupportModalOpen}
        onClose={() => setIsSupportModalOpen(false)}
        title="Contact Support"
      />

      <DisconnectPageModal
        isOpen={!!pageToDisconnect}
        onCancel={() => setPageToDisconnect(null)}
        onConfirm={() => {
          if (userData?.user?._id && pageToDisconnect?.id) {
            disconnectMutation.mutate({
              pageId: pageToDisconnect.id,
              userId: userData.user._id,
            })
          }
        }}
        page={pageToDisconnect}
      />
    </div>
  )
}
